import {
  Outlet,
  createFileRoute,
  redirect,
  retainSearchParams,
  stripSearchParams,
} from "@tanstack/react-router";
import { z } from "zod/v4";
import {
  ChevronDown,
  ChevronUp,
  Linkedin,
  Menu,
  Twitter,
  X,
} from "lucide-react";
import { useState } from "react";
import { saveReferralCode } from "./auth/-utils";
import type { ReactNode } from "react";
import { LoadingSpinner } from "@/components/loading";
import { userInfoQueryOptions } from "@/lib/queries/userInfo";
import { PolygrafLogo } from "@/components/icons/logo";
import { api } from "@/lib/http";
import { logout } from "@/lib/auth";
import {
  Tooltip,
  TooltipContent,
  TooltipProvider,
  TooltipTrigger,
} from "@/components/ui/tooltip";
import { useTheme } from "@/components/themes/provider";
import { C2PALogo } from "@/components/icons/c2pa";

const authLayoutSearchSchema = z.object({
  redirect: z.string().optional(),
  redirectSearch: z.record(z.string().min(1), z.any()).optional(),
  code: z.coerce.string().optional().describe("AppSumo activation token"),

  buyPlanId: z.uuid().optional(),
  buyPlanCoupon: z.string().optional(),

  referral_code: z.string().optional(),
});
export const Route = createFileRoute("/auth")({
  validateSearch: authLayoutSearchSchema,
  search: {
    middlewares: [
      retainSearchParams(["code", "redirect", "buyPlanId", "buyPlanCoupon"]),
      stripSearchParams({
        redirect: "/",
        redirectSearch: {},
      }),
    ],
  },
  loaderDeps: ({
    search: { redirect: redirectPath, redirectSearch, code },
  }) => ({
    redirectPath,
    redirectSearch,
    code,
  }),
  beforeLoad({ search: { referral_code } }) {
    if (referral_code) {
      saveReferralCode(referral_code);
    }
  },
  async loader({
    context: { queryClient },
    deps: { redirectPath, redirectSearch, code },
    preload,
  }) {
    const userInfo = await queryClient.ensureQueryData(userInfoQueryOptions);
    if (userInfo.state === "logged_in") {
      if (code) {
        const activateAppsumoRes = await api.auth.post(
          "appsumo/license-activation",
          {
            json: { appsumo_code: code },
            throwHttpErrors: false,
          },
        );
        if (activateAppsumoRes.ok)
          throw redirect({
            to: "/account",
            search: {
              tab: "subscription",
            },
          });
      } else
        throw redirect({ to: redirectPath ?? "/", search: redirectSearch });
    } else if (!preload) logout();
  },
  pendingComponent: () => (
    <div className="flex h-screen w-screen grow items-center justify-center">
      <LoadingSpinner size={48} />
    </div>
  ),
  component: AuthLayout,
});

type NavigationItem = {
  name: string;
  child?: { desktop: ReactNode; mobile: ReactNode };
  link?: string;
};

function ProductsDropdownDesktop() {
  const [tab, setTab] = useState<"privacy" | "provenance" | "generation">(
    "privacy",
  );

  return (
    <div className="flex w-[900px] overflow-clip rounded-lg border-1 border-gray-200 dark:border-gray-600">
      {/* Left Menu */}
      <div className="w-[400px] p-2 dark:bg-[#0C0E12]">
        <div className="flex flex-col">
          {tabs.map((item) => (
            <a href={item.link} target="_blank" className="cursor-pointer">
              <button
                key={item.key}
                onMouseEnter={() => setTab(item.key as typeof tab)}
                className={`flex items-start gap-3 rounded-2xl px-6 py-5 text-left transition-colors hover:bg-gray-100 dark:hover:bg-[#22262F] ${
                  tab === item.key ? "bg-gray-100 dark:bg-[#22262F]" : ""
                }`}
              >
                <img src={item.icon} alt={item.title} className="mt-1" />
                <div>
                  <h4 className="text-base font-semibold text-gray-900 dark:text-white">
                    {item.title}
                  </h4>
                  <p className="text-sm text-gray-600 dark:text-white">
                    {item.desc}
                  </p>
                </div>
              </button>
            </a>
          ))}
        </div>
      </div>

      {/* Right Menu */}
      <div className="w-[500px] p-2 dark:bg-[#13161B]">
        {tab === "privacy" && (
          <div className="space-y-4">
            {[
              {
                title: "Secure LLM Chat",
                desc: "Wrap AI tools with redaction, anonymization, and audit trails.",
                icon: "/icons/header/secure_llm.svg",
                link: "https://polygraf.ai/secure-llm-chat/",
              },
              {
                title: "File System Scan",
                desc: "Scan Files and Folders Instantly for Data Leak Risks.",
                icon: "/icons/header/file_system_scan.svg",
                link: "https://polygraf.ai/file-system-scan/",
              },
              {
                title: "Secret Marker",
                desc: "Discover and protect your sensitive data everywhere.",
                icon: "/icons/header/redactor.svg",
                link: "https://polygraf.ai/document-redactor/",
              },
            ].map((item) => (
              <a href={item.link} target="_blank" className="cursor-pointer">
                <div
                  key={item.title}
                  className="mt-3 cursor-pointer rounded-xl border border-gray-200 p-4 shadow-sm transition hover:border-pink-300 hover:bg-pink-50 dark:border-[#373A41] dark:hover:border-[#DD2590] dark:hover:bg-[#2c1326]"
                >
                  <div className="flex items-start gap-3">
                    <div>
                      <img
                        src={item.icon}
                        alt={item.title}
                        className="h-7 w-7"
                      />
                    </div>
                    <div>
                      <h3 className="mb-1 font-semibold text-gray-900 dark:text-white">
                        {item.title}
                      </h3>
                      <p className="text-xs text-gray-600 dark:text-[#94979C]">
                        {item.desc}
                      </p>
                    </div>
                  </div>
                </div>
              </a>
            ))}
          </div>
        )}

        {tab === "provenance" && (
          <div className="space-y-3">
            <div className="grid grid-cols-2 gap-3">
              {[
                {
                  title: "AI Content Detector",
                  desc: "Identify which AI models were used to generate the content.",
                  icon: "/icons/header/header-shuriken.svg",
                  link: "https://polygraf.ai/ai-detector/",
                },
                {
                  title: "Writing Analysis",
                  desc: "Analyze writing patterns to determine authorship.",
                  icon: "/icons/header/chart-analysis.svg",
                  link: "https://polygraf.ai/writing-analysis/",
                },
                {
                  title: "AI Highlighter",
                  desc: "Automatically highlight AI-generated content.",
                  icon: "/icons/header/highlighter.svg",
                  link: "https://polygraf.ai/ai-highlighter/",
                },
                {
                  title: "Copyright Detector",
                  desc: "Identify copyrighted material within documents.",
                  icon: "/icons/header/copyright.svg",
                  link: "https://polygraf.ai/copyright-detector/",
                },
                {
                  title: "Humanized Text Finder",
                  desc: "Spot human edits in AI-generated text.",
                  icon: "/icons/header/person.svg",
                  link: "https://polygraf.ai/humanized-text-detector/",
                },
                {
                  title: "Plagiarism Detector",
                  desc: "Maintain content integrity by detecting plagiarism.",
                  icon: "/icons/header/book-saved.svg",
                  link: "https://polygraf.ai/plagiarism-detector/",
                },
              ].map((item, index) => (
                <a href={item.link} target="_blank" className="cursor-pointer">
                  <div
                    key={index}
                    className="cursor-pointer rounded-xl border border-gray-200 p-3 shadow-sm transition hover:border-pink-300 hover:bg-pink-50 dark:border-[#373A41] dark:hover:border-[#DD2590] dark:hover:bg-[#2c1326]"
                  >
                    <div className="flex items-start gap-2">
                      <img
                        src={item.icon}
                        alt={item.title}
                        className="h-7 w-7"
                      />
                      <div>
                        <h3 className="mb-1 text-sm font-semibold text-gray-900 dark:text-white">
                          {item.title}
                        </h3>
                        <p className="text-xs text-gray-600 dark:text-[#94979C]">
                          {item.desc}
                        </p>
                      </div>
                    </div>
                  </div>
                </a>
              ))}
            </div>
            {/* Extra Card */}
            <div className="w-1/2 pt-2">
              <a
                href="https://polygraf.ai/vexon-voice-detector/"
                target="_blank"
                className="cursor-pointer"
              >
                <div className="cursor-pointer rounded-xl border border-gray-200 p-3 shadow-sm transition hover:border-pink-300 hover:bg-pink-50 dark:border-[#373A41] dark:hover:border-[#DD2590] dark:hover:bg-[#2c1326]">
                  <div className="flex items-start gap-2">
                    <img
                      src="/icons/header/vexonnn.svg"
                      alt="Vexon"
                      className="h-7 w-7"
                    />

                    <div>
                      <h3 className="mb-1 text-sm font-semibold text-gray-900 dark:text-white">
                        VeXon Voice Detector
                      </h3>
                      <p className="text-xs text-gray-600 dark:text-[#94979C]">
                        Detect synthetic voices from AI tools and deepfakes.
                      </p>
                    </div>
                  </div>
                </div>
              </a>
            </div>
          </div>
        )}

        {tab === "generation" && (
          <div className="space-y-4">
            {[
              {
                title: "Stealth AI Writer",
                desc: "Create high-quality content with built-in detection and research.",
                icon: "/icons/header/stealth.svg",
                link: "https://polygraf.ai/stealth-ai-writer/",
              },
              {
                title: "Content Enhancer",
                desc: "Improve and enhance existing content with AI support.",
                icon: "/icons/header/enhancer.svg",
                link: "https://polygraf.ai/content-enhancer/",
              },
              {
                title: "AI Humanizer (Beta)",
                desc: "Make AI-Generated Content Sound Human.",
                icon: "/icons/header/humanizer.svg",
                link: "https://polygraf.ai/content-humanizer/",
              },
            ].map((item) => (
              <a href={item.link} target="_blank" className="cursor-pointer">
                <div
                  key={item.title}
                  className="mt-3 cursor-pointer rounded-xl border border-gray-200 p-4 shadow-sm transition hover:border-pink-300 hover:bg-pink-50 dark:border-[#373A41] dark:hover:border-[#DD2590] dark:hover:bg-[#2c1326]"
                >
                  <div className="flex items-start gap-3">
                    <img src={item.icon} alt={item.title} />
                    <div>
                      <h3 className="mb-1 font-semibold text-gray-900 dark:text-white">
                        {item.title}
                      </h3>
                      <p className="w-full text-[12px] text-gray-600 dark:text-[#94979C]">
                        {item.desc}
                      </p>
                    </div>
                  </div>
                </div>
              </a>
            ))}
          </div>
        )}
      </div>
    </div>
  );
}

function SolutionsDropdownDesktop() {
  const [tab, setTab] = useState<"solutions" | "regulations">("solutions");

  return (
    <div className="flex w-[600px] overflow-clip rounded-lg border-1 border-gray-200 dark:border-gray-600">
      {/* Left Tabs */}
      <div className="w-1/2 p-2 dark:bg-[#0C0E12]">
        {menuItems.map((item) => (
          <a href={item.link} target="_blank" className="cursor-pointer">
            <button
              key={item.key}
              onMouseEnter={() =>
                setTab(item.key as "solutions" | "regulations")
              }
              className={`flex items-start gap-3 rounded-2xl px-6 py-5 text-left transition-colors hover:bg-gray-100 dark:hover:bg-[#22262F] ${
                tab === item.key ? "bg-gray-100 dark:bg-[#22262F]" : ""
              }`}
            >
              {item.icon}
              <div>
                <h4 className="text-base font-semibold text-gray-900 dark:text-white">
                  {item.title}
                </h4>
                <p className="text-sm text-gray-600 dark:text-white">
                  {item.description}
                </p>
              </div>
            </button>
          </a>
        ))}
      </div>

      {/* Right Content */}
      <div className="w-1/2 p-2 dark:bg-[#13161B]">
        {tab === "solutions" ? (
          <div className="space-y-1">
            {solutions.map(({ icon, link, label }) => (
              <a href={link} target="_blank" className="cursor-pointer">
                <div
                  key={label}
                  className="mt-3 cursor-pointer rounded-xl border border-gray-200 p-4 shadow-sm transition hover:border-pink-300 hover:bg-pink-50 dark:border-[#373A41] dark:hover:border-[#DD2590] dark:hover:bg-[#2c1326]"
                >
                  <div className="flex items-center gap-3">
                    <div className="flex h-8 w-8 flex-shrink-0 items-center justify-center">
                      {icon}
                    </div>
                    <h3 className="mb-1 font-semibold text-gray-900 dark:text-white">
                      {label}
                    </h3>
                  </div>
                </div>
              </a>
            ))}
          </div>
        ) : (
          <div className="space-y-3">
            <div className="grid grid-cols-2 gap-3">
              {regulations.map(({ link, label }) => (
                <a href={link} target="_blank" className="cursor-pointer">
                  <div
                    key={label}
                    className="cursor-pointer rounded-xl border border-gray-200 p-3 shadow-sm transition hover:border-pink-300 hover:bg-pink-50 dark:border-[#373A41] dark:hover:border-[#DD2590] dark:hover:bg-[#2c1326]"
                  >
                    <div className="flex items-center gap-2">
                      <img
                        src="/icons/header/document-check.svg"
                        alt="Document Check Icon"
                        className="h-7 w-7"
                      />
                      <h3 className="mb-1 text-sm font-semibold text-gray-900 dark:text-white">
                        {label}
                      </h3>
                    </div>
                  </div>
                </a>
              ))}
            </div>
          </div>
        )}
      </div>
    </div>
  );
}

function DevelopersDropdownDesktop() {
  return (
    <div className="flex w-[400px] overflow-clip rounded-lg border-1 border-gray-200 dark:border-gray-600 dark:bg-[#13161B]">
      {/* Right Content */}
      <div className="w-full p-2">
        <div className="space-y-1">
          <a
            href="https://polygraf.ai/api-agents/"
            target="_blank"
            className="cursor-pointer"
          >
            <div className="cursor-pointer rounded-xl border border-gray-200 p-3 shadow-sm transition hover:border-pink-300 hover:bg-pink-50 dark:border-[#373A41] dark:hover:border-[#DD2590] dark:hover:bg-[#2c1326]">
              <div className="flex items-start gap-3">
                <div>
                  <img
                    src="/icons/header/attach.svg"
                    alt="Attach Icon"
                    className="h-7 w-7"
                  />
                </div>
                <div>
                  <h3 className="mb-1 font-semibold text-gray-900 dark:text-white">
                    APIs
                  </h3>
                  <p className="text-xs text-gray-600 dark:text-[#94979C]">
                    The latest industry new and guides curated by our expert
                    team.
                  </p>
                </div>
              </div>
            </div>
          </a>
        </div>
      </div>
    </div>
  );
}

function AboutUsDropdownDesktop() {
  return (
    <div className="flex w-[500px] overflow-clip rounded-lg border-1 border-gray-200 dark:border-gray-600">
      {/* Right Content */}
      <div className="w-full p-2 dark:bg-[#13161B]">
        <div className="space-y-3">
          <div className="grid grid-cols-2 gap-3">
            {aboutUs.map(({ icon, label, link }) => (
              <a href={link} target="_blank" className="cursor-pointer">
                <div
                  key={label}
                  className="cursor-pointer rounded-xl border border-gray-200 p-3 shadow-sm transition hover:border-pink-300 hover:bg-pink-50 dark:border-[#373A41] dark:hover:border-[#DD2590] dark:hover:bg-[#2c1326]"
                >
                  <div className="flex items-center gap-2">
                    {icon}
                    <h3 className="mb-1 text-sm font-semibold text-gray-900 dark:text-white">
                      {label}
                    </h3>
                  </div>
                </div>
              </a>
            ))}
          </div>
        </div>
      </div>
    </div>
  );
}

const tabs = [
  {
    key: "privacy",
    title: "Data Privacy",
    desc: "Protect privacy & confidentiality in any environment.",
    icon: "/icons/header/privacy.svg",
    link: "https://polygraf.ai/data-privacy/",
  },
  {
    key: "provenance",
    title: "Data Provenance",
    desc: "Identify Groundtruth & authenticate content.",
    icon: "/icons/header/provenance.svg",
    link: "https://polygraf.ai/data-provenance/",
  },
  {
    key: "generation",
    title: "Content Generation",
    desc: "Prevent data leakage & hallucinations when using Gen AI.",
    icon: "/icons/header/content.svg",
    link: "https://polygraf.ai/content-generation/",
  },
];

// Now define the data arrays used in the component
const menuItems = [
  {
    key: "solutions",
    icon: (
      <img
        src="/icons/header/lamp.svg"
        alt="Solutions"
        className="mt-1 h-8 w-8 text-gray-500"
      />
    ),
    title: "Our Solutions",
    description:
      "Tailored AI for the unique compliance, security, and operational needs of each industry.",
    link: "https://polygraf.ai/our-solutions/",
  },
  {
    key: "regulations",
    icon: (
      <img
        src="/icons/header/document-copy.svg"
        alt="Regulations"
        className="mt-1 h-8 w-8 text-gray-500"
      />
    ),
    title: "Regulations",
    description:
      "Your guide to AI regulations, compliance, and global standards.",
    link: "https://polygraf.ai/regulations/",
  },
];

const solutions = [
  {
    icon: (
      <img
        src="/icons/header/compliance2.svg"
        alt="Compliance Icon"
        className="h-7 w-7 text-gray-900"
      />
    ),
    label: "Compliance",
    link: "https://polygraf.ai/compliance/",
  },
  {
    icon: (
      <img
        src="/icons/header/national-security.svg"
        alt="National Security Icon"
        className="h-7 w-7 text-gray-900"
      />
    ),
    label: "Defense & National Security",
    link: "https://polygraf.ai/defense-national-security/",
  },
  {
    icon: (
      <img
        src="/icons/header/compliance2.svg"
        alt="Compliance Icon"
        className="h-7 w-7 text-gray-900"
      />
    ),
    label: "Finance",
    link: "https://polygraf.ai/finance/",
  },
  {
    icon: (
      <img
        src="/icons/header/hr.svg"
        alt="Human Resources Icon"
        className="h-7 w-7 text-gray-900"
      />
    ),
    label: "Human Resources",
    link: "https://polygraf.ai/human-resources/",
  },
  {
    icon: (
      <img
        src="/icons/header/healthcare.svg"
        alt="Health Care Icon"
        className="h-7 w-7 text-gray-900"
      />
    ),
    label: "Health Care",
    link: "https://polygraf.ai/healthcare/",
  },
  {
    icon: (
      <img
        src="/icons/header/insurance.svg"
        alt="Insurance Icon"
        className="h-7 w-7 text-gray-900"
      />
    ),
    label: "Insurance",
    link: "https://polygraf.ai/insurance/",
  },
  {
    icon: (
      <img
        src="/icons/header/engineering.svg"
        alt="Software Development Icon"
        className="h-7 w-7 text-gray-900"
      />
    ),
    label: "Software Development",
    link: "https://polygraf.ai/software-development/",
  },
  {
    icon: (
      <img
        src="/icons/header/marketing.svg"
        alt="Marketing Icon"
        className="h-7 w-7 text-gray-900"
      />
    ),
    label: "Marketing",
    link: "https://polygraf.ai/marketing/",
  },
];

const regulations = [
  { link: "https://polygraf.ai/california-privacy-rights-act/", label: "CCPA" },
  { link: "https://polygraf.ai/eu-ai-act/", label: "EU AI Act" },
  { link: "https://polygraf.ai/gdpr/", label: "GDPR" },
  { link: "https://polygraf.ai/hipaa/", label: "HIPAA" },
  { link: "https://polygraf.ai/utah-ai-policy-act/", label: "Utah AI..." },
];

const aboutUs = [
  {
    icon: (
      <img
        src="/icons/header/logomark.svg"
        alt="Polygraf Icon"
        className="h-7 w-7 text-gray-900"
      />
    ),
    label: "About Us",
    link: "https://polygraf.ai/about/",
  },
  {
    icon: (
      <img
        src="/icons/header/careers.svg"
        alt="Careers Icon"
        className="h-7 w-7 text-gray-900"
      />
    ),
    label: "Careers",
    link: "https://polygraf.ai/careers/",
  },
  {
    icon: (
      <img
        src="/icons/header/World.svg"
        alt="World Icon"
        className="h-7 w-7 text-gray-900"
      />
    ),
    label: "News & More",
    link: "https://polygraf.ai/news/",
  },
  {
    icon: (
      <img
        src="/icons/header/contact.svg"
        alt="Contact Us Icon"
        className="h-7 w-7 text-gray-900"
      />
    ),
    label: "Contact us",
    link: "https://polygraf.ai/contact/",
  },
  {
    icon: (
      <img
        src="/icons/header/World.svg"
        alt="World Icon"
        className="h-7 w-7 text-gray-900"
      />
    ),
    label: "Events",
    link: "https://polygraf.ai/events/",
  },
];

const navigationItems: Array<NavigationItem> = [
  {
    name: "Products",
    child: {
      desktop: <ProductsDropdownDesktop />,
      mobile: (
        <div className="space-y-2">
          <h4 className="mb-2 font-semibold">Data Privacy</h4>
          <ul className="space-y-2">
            <li>
              <a
                href="#"
                className="block rounded px-2 py-1 hover:bg-gray-200 dark:hover:bg-gray-700"
              >
                Secure LLM Chat
              </a>
            </li>
            <li>
              <a
                href="#"
                className="block rounded px-2 py-1 hover:bg-gray-200 dark:hover:bg-gray-700"
              >
                File System Scan
              </a>
            </li>
            <li>
              <a
                href="#"
                className="block rounded px-2 py-1 hover:bg-gray-200 dark:hover:bg-gray-700"
              >
                Secret Marker
              </a>
            </li>
          </ul>

          <h4 className="mt-4 mb-2 font-semibold">Data Provenance</h4>
          <ul className="space-y-2">
            <li>
              <a
                href="#"
                className="block rounded px-2 py-1 hover:bg-gray-200 dark:hover:bg-gray-700"
              >
                AI Content Detector
              </a>
            </li>
            <li>
              <a
                href="#"
                className="block rounded px-2 py-1 hover:bg-gray-200 dark:hover:bg-gray-700"
              >
                Writing Analysis
              </a>
            </li>
            <li>
              <a
                href="#"
                className="block rounded px-2 py-1 hover:bg-gray-200 dark:hover:bg-gray-700"
              >
                Copyright Detector
              </a>
            </li>
          </ul>

          <h4 className="mt-4 mb-2 font-semibold">Content Generation</h4>
          <ul className="space-y-2">
            <li>
              <a
                href="#"
                className="block rounded px-2 py-1 hover:bg-gray-200 dark:hover:bg-gray-700"
              >
                Stealth AI Writer
              </a>
            </li>
            <li>
              <a
                href="#"
                className="block rounded px-2 py-1 hover:bg-gray-200 dark:hover:bg-gray-700"
              >
                Content Enhancer
              </a>
            </li>
            <li>
              <a
                href="#"
                className="block rounded px-2 py-1 hover:bg-gray-200 dark:hover:bg-gray-700"
              >
                AI Humanizer (Beta)
              </a>
            </li>
          </ul>
        </div>
      ),
    },
  },
  {
    name: "Solutions",
    child: {
      desktop: <SolutionsDropdownDesktop />,
      mobile: (
        <div className="space-y-3">
          <h4 className="mb-2 font-semibold">Our Solutions</h4>
          <ul className="space-y-2">
            {solutions.map(({ label }) => (
              <li key={label}>
                <a
                  href="#"
                  className="block rounded px-2 py-1 hover:bg-gray-200 dark:hover:bg-gray-700"
                >
                  {label}
                </a>
              </li>
            ))}
          </ul>

          <h4 className="mt-4 mb-2 font-semibold">Regulations</h4>
          <ul className="space-y-2">
            {regulations.map(({ link, label }) => (
              <li key={label}>
                <a
                  href={link}
                  className="block rounded px-2 py-1 hover:bg-gray-200 dark:hover:bg-gray-700"
                >
                  {label}
                </a>
              </li>
            ))}
          </ul>
        </div>
      ),
    },
  },
  {
    name: "Partners",
    link: "https://polygraf.ai/partner/",
  },
  {
    name: "Developers",
    child: {
      desktop: <DevelopersDropdownDesktop />,
      mobile: (
        <div className="space-y-2">
          <ul>
            <li>
              <a
                href="#"
                className="block rounded px-2 py-1 hover:bg-gray-200 dark:hover:bg-gray-700"
              >
                APIs
              </a>
            </li>
            <li>
              <a
                href="#"
                className="block rounded px-2 py-1 hover:bg-gray-200 dark:hover:bg-gray-700"
              >
                Documentation
              </a>
            </li>
            <li>
              <a
                href="#"
                className="block rounded px-2 py-1 hover:bg-gray-200 dark:hover:bg-gray-700"
              >
                Integration Guides
              </a>
            </li>
          </ul>
        </div>
      ),
    },
  },
  {
    name: "About Us",
    child: {
      desktop: <AboutUsDropdownDesktop />,
      mobile: (
        <div className="space-y-2">
          <ul>
            {aboutUs.map(({ label }) => (
              <li key={label}>
                <a
                  href="#"
                  className="block rounded px-2 py-1 hover:bg-gray-200 dark:hover:bg-gray-700"
                >
                  {label}
                </a>
              </li>
            ))}
          </ul>
        </div>
      ),
    },
  },
];

function AuthLayout() {
  const [isMobileMenuOpen, setIsMobileMenuOpen] = useState(false);
  const [openMobileItem, setOpenMobileItem] = useState<string | null>(null);

  const toggleMobileItem = (name: string) => {
    if (openMobileItem === name) {
      setOpenMobileItem(null);
    } else {
      setOpenMobileItem(name);
    }
  };

  return (
    <>
      <div className="flex flex-1 flex-col py-4">
        <header className="w-fulldark:bg-transparent">
          <div className="px-4 sm:px-6 lg:px-8">
            <div className="flex h-16 items-center justify-between">
              <div className="flex h-20 flex-col items-center justify-center p-4 dark:border-gray-800">
                <a href="https://polygraf.ai">
                  <PolygrafLogo />
                </a>
                <span className="block text-xs fill-mode-forwards display-in-unset display-out-none zoom-in zoom-out group-data-[state=collapsed]/sidebar:animate-out group-data-[state=open]/sidebar:animate-in">
                  Clarity within Chaos®
                </span>
              </div>
              {/* Desktop Navigation */}
              <nav className="hidden items-center space-x-8 md:flex">
                <TooltipProvider delayDuration={10}>
                  {navigationItems.map((item) => (
                    <Tooltip key={item.name}>
                      <TooltipTrigger className="group relative">
                        <div className="flex items-center space-x-1 text-black transition-colors duration-200 hover:text-gray-300">
                          {item.link ? (
                            <a
                              className="text-sm font-medium dark:text-white"
                              href={item.link}
                              target="_blank"
                            >
                              {item.name}
                            </a>
                          ) : (
                            <span className="text-sm font-medium dark:text-white">
                              {item.name}
                            </span>
                          )}
                          {item.child && (
                            <ChevronDown className="h-4 w-4 dark:text-white" />
                          )}
                        </div>
                      </TooltipTrigger>
                      {item.child && (
                        <TooltipContent
                          side="bottom"
                          className="mt-4 w-full bg-background"
                        >
                          {item.child.desktop}
                        </TooltipContent>
                      )}
                    </Tooltip>
                  ))}
                </TooltipProvider>
              </nav>

              {/* Desktop Auth Buttons */}
              <div className="mr-32 hidden items-center space-x-4 md:flex">
                <div className="font-medium text-gray-700 hover:text-gray-900"></div>
              </div>
              {/* Mobile menu button */}
              <button
                className="p-2 text-gray-700 hover:text-gray-900 md:hidden"
                onClick={() => setIsMobileMenuOpen(!isMobileMenuOpen)}
              >
                {isMobileMenuOpen ? (
                  <X className="h-6 w-6" />
                ) : (
                  <Menu className="h-6 w-6" />
                )}
              </button>
            </div>

            {/* Mobile Navigation */}
            {isMobileMenuOpen && (
              <div className="fixed inset-0 z-50 bg-[#13161B] md:hidden">
                <div className="flex h-16 items-center justify-between px-6">
                  <div className="text-xl font-medium text-white">Products</div>
                  <button onClick={() => setIsMobileMenuOpen(false)}>
                    <X className="h-6 w-6 text-white" />
                  </button>
                </div>
                <div className="h-[calc(100%-4rem)] overflow-y-auto">
                  <div className="flex h-full flex-col px-4 py-4">
                    <div className="space-y-4">
                      {/* Data Privacy Section */}
                      <div>
                        <button
                          onClick={() => toggleMobileItem("Data Privacy")}
                          className="flex w-full items-center justify-between px-2 py-3 text-white"
                        >
                          <div className="flex items-center space-x-3">
                            <img
                              src="/icons/header/privacy.svg"
                              alt="Shield Icon"
                              className="h-8 w-8 text-white"
                            />
                            <span className="text-lg">Data Privacy</span>
                          </div>
                          {openMobileItem === "Data Privacy" ? (
                            <ChevronUp className="h-5 w-5 text-gray-400" />
                          ) : (
                            <ChevronDown className="h-5 w-5 text-gray-400" />
                          )}
                        </button>

                        <div
                          className={`ml-10 space-y-3 overflow-hidden transition-all duration-300 ease-in-out ${
                            openMobileItem === "Data Privacy"
                              ? "max-h-96 pt-2 opacity-100"
                              : "max-h-0 opacity-0"
                          }`}
                        >
                          <a
                            href="https://polygraf.ai/secure-llm-chat/"
                            target="_blank"
                            className="flex items-center space-x-3 text-white"
                          >
                            <img
                              src="/icons/header/secure_llm.svg"
                              alt="Secure LLM Chat"
                              className="h-7 w-7 text-gray-400"
                            />
                            <span>Secure LLM Chat</span>
                          </a>
                          <a
                            href="https://polygraf.ai/file-system-scan/"
                            target="_blank"
                            className="flex items-center space-x-3 text-white"
                          >
                            <img
                              src="/icons/header/file_system_scan.svg"
                              alt="File System Scan"
                              className="h-7 w-7 text-gray-400"
                            />
                            <span>File System Scan</span>
                          </a>
                          <a
                            href="https://polygraf.ai/document-redactor/"
                            target="_blank"
                            className="flex items-center space-x-3 text-white"
                          >
                            <img
                              src="/icons/header/redactor.svg"
                              alt="Secure LLM Chat"
                              className="h-7 w-7 text-gray-400"
                            />
                            <span>Document Redactor</span>
                          </a>
                        </div>
                      </div>

                      {/* Data Provenance Section */}
                      <div>
                        <button
                          onClick={() => toggleMobileItem("Data Provenance")}
                          className="flex w-full items-center justify-between px-2 py-3 text-white"
                        >
                          <div className="flex items-center space-x-3">
                            <img
                              src="/icons/header/provenance.svg"
                              alt="Data Provenance Icon"
                              className="h-8 w-8 text-white"
                            />
                            <span className="text-lg">Data Provenance</span>
                          </div>
                          {openMobileItem === "Data Provenance" ? (
                            <ChevronUp className="h-5 w-5 text-gray-400" />
                          ) : (
                            <ChevronDown className="h-5 w-5 text-gray-400" />
                          )}
                        </button>

                        <div
                          className={`ml-10 space-y-3 overflow-hidden transition-all duration-300 ease-in-out ${
                            openMobileItem === "Data Provenance"
                              ? "max-h-96 pt-2 opacity-100"
                              : "max-h-0 opacity-0"
                          }`}
                        >
                          <a
                            href="https://polygraf.ai/ai-detector/"
                            target="_blank"
                            className="flex items-center space-x-3 text-white"
                          >
                            <img
                              src="/icons/header/header-shuriken.svg"
                              alt="AI Content Detector"
                              className="h-7 w-7 text-gray-400"
                            />
                            <span>AI Content Detector</span>
                          </a>
                          <a
                            href="https://polygraf.ai/writing-analysis/"
                            target="_blank"
                            className="flex items-center space-x-3 text-white"
                          >
                            <img
                              src="/icons/header/chart-analysis.svg"
                              alt="Writing Analysis"
                              className="h-7 w-7 text-gray-400"
                            />
                            <span>Writing Analysis</span>
                          </a>
                          <a
                            href="https://polygraf.ai/ai-highlighter/"
                            target="_blank"
                            className="flex items-center space-x-3 text-white"
                          >
                            <img
                              src="/icons/header/highlighter.svg"
                              alt="AI Highlighter"
                              className="h-7 w-7 text-gray-400"
                            />
                            <span>AI Highlighter</span>
                          </a>
                          <a
                            href="https://polygraf.ai/copyright-detector/"
                            target="_blank"
                            className="flex items-center space-x-3 text-white"
                          >
                            <img
                              src="/icons/header/copyright.svg"
                              alt="Copyright Detector"
                              className="h-7 w-7 text-gray-400"
                            />
                            <span>Copyright Detector</span>
                          </a>
                          <a
                            href="https://polygraf.ai/humanized-text-detector/"
                            target="_blank"
                            className="flex items-center space-x-3 text-white"
                          >
                            <img
                              src="/icons/header/person.svg"
                              alt="Humanized Text Finder"
                              className="h-7 w-7 text-gray-400"
                            />
                            <span>Humanized Text Finder</span>
                          </a>
                          <a
                            href="https://polygraf.ai/plagiarism-detector/"
                            target="_blank"
                            className="flex items-center space-x-3 text-white"
                          >
                            <img
                              src="/icons/header/book-saved.svg"
                              alt="Plagiarism Detector"
                              className="h-7 w-7 text-gray-400"
                            />
                            <span>Plagiarism Detector</span>
                          </a>
                          <a
                            href="https://polygraf.ai/vexon-voice-detector/"
                            target="_blank"
                            className="flex items-center space-x-3 text-white"
                          >
                            <img
                              src="/icons/header/vexonnn.svg"
                              alt="VeXon Voice Detector"
                              className="h-7 w-7 text-gray-400"
                            />
                            <span>VeXon Voice Detector</span>
                          </a>
                        </div>
                      </div>

                      {/* Content Generation Section */}
                      <div>
                        <button
                          onClick={() => toggleMobileItem("Content Generation")}
                          className="flex w-full items-center justify-between px-2 py-3 text-white"
                        >
                          <div className="flex items-center space-x-3">
                            <img
                              src="/icons/header/content.svg"
                              alt="Content Generation Icon"
                              className="h-8 w-8 text-white"
                            />
                            <span className="text-lg">Content Generation</span>
                          </div>
                          {openMobileItem === "Content Generation" ? (
                            <ChevronUp className="h-5 w-5 text-gray-400" />
                          ) : (
                            <ChevronDown className="h-5 w-5 text-gray-400" />
                          )}
                        </button>

                        <div
                          className={`ml-10 space-y-3 overflow-hidden transition-all duration-300 ease-in-out ${
                            openMobileItem === "Content Generation"
                              ? "max-h-96 pt-2 opacity-100"
                              : "max-h-0 opacity-0"
                          }`}
                        >
                          <a
                            href="https://polygraf.ai/stealth-ai-writer/"
                            target="_blank"
                            className="flex items-center space-x-3 text-white"
                          >
                            <img
                              src="/icons/header/stealth.svg"
                              alt="Stealth AI Writer"
                              className="h-7 w-7 text-gray-400"
                            />
                            <span>Stealth AI Writer</span>
                          </a>
                          <a
                            href="https://polygraf.ai/content-enhancer/"
                            target="_blank"
                            className="flex items-center space-x-3 text-white"
                          >
                            <img
                              src="/icons/header/enhancer.svg"
                              alt="Content Enhancer"
                              className="h-7 w-7 text-gray-400"
                            />
                            <span>Content Enhancer</span>
                          </a>
                          <a
                            href="https://polygraf.ai/content-humanizer/"
                            target="_blank"
                            className="flex items-center space-x-3 text-white"
                          >
                            <img
                              src="/icons/header/humanizer.svg"
                              alt="AI Humanizer (Beta)"
                              className="h-7 w-7 text-gray-400"
                            />
                            <span>AI Humanizer (Beta)</span>
                          </a>
                        </div>
                      </div>

                      {/* Solutions Title */}
                      <div className="pt-3">
                        <h3 className="px-2 text-sm font-medium text-gray-400">
                          Solutions
                        </h3>
                      </div>

                      {/* Our Solutions Section */}
                      <div>
                        <button
                          onClick={() => toggleMobileItem("Our Solutions")}
                          className="flex w-full items-center justify-between px-2 py-3 text-white"
                        >
                          <div className="flex items-center space-x-3">
                            <img
                              src="/icons/header/lamp.svg"
                              alt="Solutions"
                              className="h-8 w-8"
                            />
                            <span className="text-lg">Our Solutions</span>
                          </div>
                          {openMobileItem === "Our Solutions" ? (
                            <ChevronUp className="h-5 w-5 text-gray-400" />
                          ) : (
                            <ChevronDown className="h-5 w-5 text-gray-400" />
                          )}
                        </button>

                        <div
                          className={`ml-10 space-y-3 overflow-hidden transition-all duration-300 ease-in-out ${
                            openMobileItem === "Our Solutions"
                              ? "max-h-[500px] pt-2 opacity-100"
                              : "max-h-0 opacity-0"
                          }`}
                        >
                          {solutions.map(({ link, icon, label }) => (
                            <a
                              key={label}
                              href={link}
                              className="flex items-center space-x-3 text-white"
                              target="_blank"
                            >
                              {icon}
                              <span>{label}</span>
                            </a>
                          ))}
                        </div>
                      </div>

                      {/* Regulations Section */}
                      <div>
                        <button
                          onClick={() => toggleMobileItem("Regulations")}
                          className="flex w-full items-center justify-between px-2 py-3 text-white"
                        >
                          <div className="flex items-center space-x-3">
                            <img
                              src="/icons/header/document-copy.svg"
                              alt="Regulations"
                              className="h-8 w-8"
                            />
                            <span className="text-lg">Regulations</span>
                          </div>
                          {openMobileItem === "Regulations" ? (
                            <ChevronUp className="h-5 w-5 text-gray-400" />
                          ) : (
                            <ChevronDown className="h-5 w-5 text-gray-400" />
                          )}
                        </button>

                        <div
                          className={`ml-10 space-y-3 overflow-hidden transition-all duration-300 ease-in-out ${
                            openMobileItem === "Regulations"
                              ? "max-h-96 pt-2 opacity-100"
                              : "max-h-0 opacity-0"
                          }`}
                        >
                          {regulations.map(({ link, label }) => (
                            <a
                              key={label}
                              href={link}
                              className="flex items-center space-x-3 text-white"
                              target="_blank"
                            >
                              <img
                                src="/icons/header/document-check.svg"
                                alt="Document Check Icon"
                                className="h-7 w-7"
                              />
                              <span>{label}</span>
                            </a>
                          ))}
                        </div>
                      </div>

                      {/* Developers Section */}
                      <div>
                        <button
                          onClick={() => toggleMobileItem("Developers")}
                          className="flex w-full items-center justify-between px-2 py-3 text-white"
                        >
                          <div className="flex items-center space-x-3">
                            <span className="text-lg">Developers</span>
                          </div>
                          {openMobileItem === "Developers" ? (
                            <ChevronUp className="h-5 w-5 text-gray-400" />
                          ) : (
                            <ChevronDown className="h-5 w-5 text-gray-400" />
                          )}
                        </button>

                        <div
                          className={`ml-10 space-y-3 overflow-hidden transition-all duration-300 ease-in-out ${
                            openMobileItem === "Developers"
                              ? "max-h-96 pt-2 opacity-100"
                              : "max-h-0 opacity-0"
                          }`}
                        >
                          <a
                            href="https://polygraf.ai/api-agents/"
                            target="_blank"
                            className="flex items-center space-x-3 text-white"
                          >
                            <img
                              src="/icons/header/attach.svg"
                              alt="Attach Icon"
                              className="h-7 w-7"
                            />
                            <span>APIs</span>
                          </a>
                        </div>
                      </div>

                      {/* About Us Section */}
                      <div>
                        <button
                          onClick={() => toggleMobileItem("About Us")}
                          className="flex w-full items-center justify-between px-2 py-3 text-white"
                        >
                          <div className="flex items-center space-x-3">
                            <span className="text-lg">About us</span>
                          </div>
                          {openMobileItem === "About Us" ? (
                            <ChevronUp className="h-5 w-5 text-gray-400" />
                          ) : (
                            <ChevronDown className="h-5 w-5 text-gray-400" />
                          )}
                        </button>

                        <div
                          className={`ml-10 space-y-3 overflow-hidden transition-all duration-300 ease-in-out ${
                            openMobileItem === "About Us"
                              ? "max-h-96 pt-2 opacity-100"
                              : "max-h-0 opacity-0"
                          }`}
                        >
                          {aboutUs.map(({ icon, label, link }) => (
                            <a
                              key={label}
                              href={link}
                              target="_blank"
                              className="flex items-center space-x-3 text-white"
                            >
                              {icon}
                              <span>{label}</span>
                            </a>
                          ))}
                        </div>
                      </div>
                    </div>
                  </div>
                </div>
              </div>
            )}
          </div>
        </header>
        <div className="mt-8 mb-8">
          <Outlet />
        </div>
      </div>
      <Footer />
    </>
  );
}

function Footer() {
  const theme = useTheme();
  return (
    <footer className="bg-gray-50 pt-4 pb-4 text-gray-600 dark:bg-transparent">
      <div className="px-4 sm:px-6 lg:px-8">
        {/* Main Footer Content */}
        <div className="mb-8 grid grid-cols-1 gap-8 md:grid-cols-2 lg:grid-cols-5">
          {/* Logo and Certifications */}
          <div className="lg:col-span-1">
            <a href="https://polygraf.ai">
              <PolygrafLogo />
            </a>

            {/* Certification Badges */}
            <div className="mt-10 grid max-w-[18rem] grid-cols-3 gap-4">
              <div className="aspect-square w-full">
                <img
                  src="/footer/duns.svg"
                  alt="DUNS logo"
                  className="h-full w-full object-contain"
                />
              </div>
              <div className="aspect-square w-full">
                <C2PALogo />
              </div>
              <div className="aspect-square w-full">
                <img
                  src={
                    theme === "dark"
                      ? "/footer/sxsw-white.webp"
                      : "/footer/sxsw-black.webp"
                  }
                  alt="SXSW Winner of 2025"
                  className="h-full w-full object-contain"
                />
              </div>
            </div>
          </div>

          {/* Data Privacy */}
          <div>
            <h3 className="mb-4 text-lg font-semibold text-gray-600">
              Data Privacy
            </h3>
            <ul className="space-y-2">
              <li className="dark:text-white">
                <a
                  href="https://polygraf.ai/secure-llm-chat/"
                  className="transition-colors hover:text-gray-500"
                >
                  Secure LLM Chat
                </a>
              </li>
              <li className="dark:text-white">
                <a
                  href="https://polygraf.ai/file-system-scan/"
                  className="transition-colors hover:text-gray-500"
                >
                  File System Scan
                </a>
              </li>
              <li className="dark:text-white">
                <a
                  href="https://polygraf.ai/document-redactor/"
                  className="transition-colors hover:text-gray-500"
                >
                  Document Redactor
                </a>
              </li>
            </ul>
          </div>

          {/* Data Provenance */}
          <div>
            <h3 className="mb-4 text-lg font-semibold text-gray-600">
              Data Provenance
            </h3>
            <ul className="space-y-2">
              <li className="dark:text-white">
                <a
                  href="https://polygraf.ai/ai-detector/"
                  className="transition-colors hover:text-gray-500"
                >
                  AI Detector
                </a>
              </li>
              <li className="dark:text-white">
                <a
                  href="https://polygraf.ai/ai-highlighter/"
                  className="transition-colors hover:text-gray-500"
                >
                  AI Highlighter
                </a>
              </li>
              <li className="dark:text-white">
                <a
                  href="https://polygraf.ai/humanized-text-detector/"
                  className="transition-colors hover:text-gray-500"
                >
                  Humanized Text Detector
                </a>
              </li>
              <li className="dark:text-white">
                <a
                  href="https://polygraf.ai/copyright-detector/"
                  className="transition-colors hover:text-gray-500"
                >
                  Copyright Detector
                </a>
              </li>
              <li className="dark:text-white">
                <a
                  href="https://polygraf.ai/plagiarism-detector/"
                  className="transition-colors hover:text-gray-500"
                >
                  Plagiarism Detector
                </a>
              </li>
              <li className="dark:text-white">
                <a
                  href="https://polygraf.ai/writing-analysis/"
                  className="transition-colors hover:text-gray-500"
                >
                  Writing Analysis
                </a>
              </li>
              <li className="dark:text-white">
                <a
                  href="https://polygraf.ai/vexon-voice-detector/"
                  className="transition-colors hover:text-gray-500"
                >
                  Vexon Voice Detector
                </a>
              </li>
            </ul>
          </div>

          {/* Content Generation */}
          <div>
            <h3 className="mb-4 text-lg font-semibold text-gray-600">
              Content Generation
            </h3>
            <ul className="space-y-2">
              <li className="dark:text-white">
                <a
                  href="https://polygraf.ai/stealth-ai-writer/"
                  className="transition-colors hover:text-gray-500"
                >
                  Stealth AI Writer
                </a>
              </li>
              <li className="dark:text-white">
                <a
                  href="https://polygraf.ai/content-enhancer/"
                  className="transition-colors hover:text-gray-500"
                >
                  Content Enhancer
                </a>
              </li>
              <li className="dark:text-white">
                <a
                  href="https://polygraf.ai/content-humanizer/"
                  className="transition-colors hover:text-gray-500"
                >
                  AI Humanizer (Beta)
                </a>
              </li>
            </ul>
          </div>

          {/* Company */}
          <div>
            <h3 className="mb-4 text-lg font-semibold text-gray-600">
              Company
            </h3>
            <ul className="space-y-2">
              <li className="dark:text-white">
                <a
                  href="https://polygraf.ai/"
                  className="transition-colors hover:text-gray-500"
                >
                  Home
                </a>
              </li>
              <li className="dark:text-white">
                <a
                  href="https://polygraf.ai/about/"
                  className="transition-colors hover:text-gray-500"
                >
                  About us
                </a>
              </li>
              <li className="dark:text-white">
                <a
                  href="https://polygraf.ai/careers/"
                  className="transition-colors hover:text-gray-500"
                >
                  Careers
                </a>
              </li>
              <li className="dark:text-white">
                <a
                  href="https://polygraf.ai/newsmore/"
                  className="transition-colors hover:text-gray-500"
                >
                  News & More
                </a>
              </li>
              <li className="dark:text-white">
                <a
                  href="https://polygraf.ai/contact/"
                  className="transition-colors hover:text-gray-500"
                >
                  Contact
                </a>
              </li>
            </ul>
          </div>
        </div>

        {/* Bottom Section */}
        <div className="border-t border-gray-200 pt-4">
          <div className="flex flex-col items-center justify-between md:flex-row">
            <div className="mb-4 flex flex-col items-center space-y-2 md:mb-0 md:flex-row md:space-y-0 md:space-x-6">
              <p className="text-sm text-gray-500">
                © 2025 Polygraf AI. All rights reserved.
              </p>
              <div className="flex space-x-4 text-sm">
                <a
                  href="https://polygraf.ai/terms-and-conditions/"
                  target="_blank"
                  className="text-gray-500 transition-colors hover:text-gray-500"
                >
                  Terms & Conditions
                </a>
                <span className="text-gray-400">|</span>
                <a
                  href="https://polygraf.ai/privacy-policy/"
                  target="_blank"
                  className="text-gray-500 transition-colors hover:text-gray-500"
                >
                  Privacy Policy
                </a>
              </div>
            </div>

            {/* Social Media Icons */}

            <div className="flex space-x-4">
              <a
                href="https://x.com/polygraf_ai?mx=2&"
                target="_blank"
                className="text-gray-500 transition-colors hover:text-gray-500"
              >
                <Twitter className="h-5 w-5" />
              </a>
              <a
                href="https://www.linkedin.com/company/polygraf-ai/"
                target="_blank"
                className="text-gray-500 transition-colors hover:text-gray-500"
              >
                <Linkedin className="h-5 w-5" />
              </a>
            </div>
          </div>
        </div>
      </div>
    </footer>
  );
}
